import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import RemoveShoppingCartRoundedIcon from '@mui/icons-material/RemoveShoppingCartRounded';
import { Stack } from '@mui/material';

import { useEngagementSettings, useUpdateEngagementSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  FeedbackSnackbar,
  NumberField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';

/** Customer engagement: reminders, abandoned cart, campaigns and limits. */
export function EngagementSettingsPage() {
  const { canView, canManage } = useDomainPermissions('engagement');
  const query = useEngagementSettings();
  const update = useUpdateEngagementSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Engagement settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Engagement settings"
      subtitle="Reminders, abandoned cart, marketing campaigns and frequency limits."
      icon={<CampaignRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Reminders" icon={<NotificationsActiveRoundedIcon />}>
              <SwitchField
                label="Reminder engine"
                checked={draft.reminderEngineEnabled}
                onChange={(value) => form.setField('reminderEngineEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Important-date reminders"
                description="Birthdays, anniversaries and similar."
                checked={draft.importantDateRemindersEnabled}
                onChange={(value) => form.setField('importantDateRemindersEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Abandoned cart" icon={<RemoveShoppingCartRoundedIcon />}>
              <SwitchField
                label="Abandoned cart recovery"
                checked={draft.abandonedCartEnabled}
                onChange={(value) => form.setField('abandonedCartEnabled', value)}
                disabled={disabled}
              />
              <NumberField
                label="Recovery delay"
                description="Minutes to wait before triggering recovery."
                value={draft.abandonedCartDelayMinutes}
                onChange={(value) => form.setField('abandonedCartDelayMinutes', value)}
                disabled={disabled || !draft.abandonedCartEnabled}
                min={0}
                suffix="min"
              />
            </SettingsCard>

            <SettingsCard title="Marketing" icon={<CampaignRoundedIcon />}>
              <SwitchField
                label="Marketing campaigns"
                checked={draft.marketingCampaignsEnabled}
                onChange={(value) => form.setField('marketingCampaignsEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Promo recommendations"
                checked={draft.promoRecommendationsEnabled}
                onChange={(value) => form.setField('promoRecommendationsEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label="Segmentation"
                checked={draft.segmentationEnabled}
                onChange={(value) => form.setField('segmentationEnabled', value)}
                disabled={disabled}
              />
              <NumberField
                label="Communication frequency limit"
                description="Maximum messages per customer per day."
                value={draft.communicationFrequencyLimitPerDay}
                onChange={(value) => form.setField('communicationFrequencyLimitPerDay', value)}
                disabled={disabled}
                min={0}
                suffix="/ day"
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
